import { useState, useRef, useEffect } from "react";
import * as Y from "yjs";
import { toUint8Array } from "js-base64";
import { invoke } from "../lib/bridge";
import { appendListItemDelta } from "../lib/listItem";
import { useYDoc } from "../hooks/useYDoc";
import { useAutoResize } from "../hooks/useAutoResize";
import { useYText } from "../hooks/useYText";
import { DocMeta } from "../App";
import { HelpTip } from "./HelpTip";
import { Button } from "./ui/Button";
import { Spinner } from "./ui/Spinner";
import { DecryptedImage } from "./DecryptedImage";
import { ChatBox } from "./ChatBox";
import { useChat } from "../hooks/useChat";
import { useVisibility } from "../hooks/useVisibility";
import { IngredientsSection, Ingredient } from "./IngredientsSection";
import { ListPickerModal } from "./ListPickerModal";
import mealHelpMd from "../help/meal.md?raw";
import { PageHeader } from "./ui/PageHeader";

interface MealViewProps {
  doc: DocMeta;
  memberId: number;
  authToken: string | null;
  apiUrl: string;
  syncVersion?: number;
  onBack: () => void;
}

export function MealView({ doc, memberId, authToken, apiUrl, syncVersion, onBack }: MealViewProps) {
  const { doc: yDoc, loading, error } = useYDoc(doc.docId, syncVersion);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { content, handleChange } = useYText(yDoc, "content");

  const [editing, setEditing] = useState(false);
  // Track explicit user intent to expand the ingredients form. Visibility is
  // derived: show when there are already ingredients OR when the user has
  // clicked "Enter Ingredients". This avoids CRDT-driven state that fights
  // with user actions on every doc reload.
  const [userExpandedIngredients, setUserExpandedIngredients] = useState(false);
  const [newMeasurement, setNewMeasurement] = useState("");
  const [newIngredient, setNewIngredient] = useState("");
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Chat
  const [members, setMembers] = useState<{ id: number; avatarUrl: string | null }[]>([]);

  useEffect(() => {
    invoke<{ id: number; avatarUrl: string | null }[]>("list_members")
      .then(setMembers).catch(() => {});
  }, []);

  const visible = useVisibility();
  const { messages, sendMessage } = useChat({
    apiUrl, authToken, channel: doc.docId, memberId, active: visible,
  });
  const memberAvatars = new Map(members.map((m) => [m.id, m.avatarUrl]));

  // List picker state
  const [pickingFor, setPickingFor] = useState<Ingredient | null>(null);
  const [lists, setLists] = useState<DocMeta[]>([]);
  const [addedMsg, setAddedMsg] = useState("");

  useAutoResize(textareaRef, content);

  // Observe ingredients array
  useEffect(() => {
    if (!yDoc) return;

    const yIngredients = yDoc.getArray("ingredients");
    const sync = () => setIngredients(yIngredients.toJSON() as Ingredient[]);
    sync();

    yIngredients.observe(sync);
    return () => yIngredients.unobserve(sync);
  }, [yDoc]);

  // Observe photo URL
  useEffect(() => {
    if (!yDoc) return;
    const yPhoto = yDoc.getText("photo_url");
    setPhotoUrl(yPhoto.toString());
    const obs = () => setPhotoUrl(yPhoto.toString());
    yPhoto.observe(obs);
    return () => yPhoto.unobserve(obs);
  }, [yDoc]);

  if (loading) return <div className="container"><Spinner /></div>;
  if (error) return <div className="container"><p>Error: {error}</p></div>;
  if (!yDoc) return null;

  // Derive section visibility from data so remote syncs never override it.
  // The section is shown when there are existing ingredients (data-driven) OR
  // when the user has explicitly clicked "Enter Ingredients" (intent-driven).
  const showIngredients = ingredients.length > 0 || userExpandedIngredients;

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !yDoc) return;

    setUploadingPhoto(true);
    try {
      const result = await invoke<{ photoUrl: string; thumbUrl: string }>("upload_photo", { file });

      yDoc.transact(() => {
        const yPhoto = yDoc.getText("photo_url");
        yPhoto.delete(0, yPhoto.toString().length);
        yPhoto.insert(0, result.photoUrl);

        const yThumb = yDoc.getText("thumb_url");
        yThumb.delete(0, yThumb.toString().length);
        yThumb.insert(0, result.thumbUrl);
      });
    } catch (err) {
      console.error("Failed to upload photo:", err);
    }
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const addIngredient = () => {
    const m = newMeasurement.trim();
    const ing = newIngredient.trim();
    if (!ing) return;

    const yIngredients = yDoc.getArray("ingredients");
    yDoc.transact(() => {
      const item = new Y.Map<unknown>();
      item.set("id", crypto.randomUUID());
      item.set("measurement", m);
      item.set("ingredient", ing);
      yIngredients.push([item]);
    });
    setNewMeasurement("");
    setNewIngredient("");
  };

  const removeIngredient = (index: number) => {
    const yIngredients = yDoc.getArray("ingredients");
    yDoc.transact(() => {
      yIngredients.delete(index, 1);
    });
  };

  const openListPicker = async (ing: Ingredient) => {
    try {
      const docs = await invoke<DocMeta[]>("list_documents", { docType: "list" });
      setLists(docs);
      setPickingFor(ing);
    } catch (e) {
      console.error("Failed to load lists:", e);
    }
  };

  const addToList = async (listDoc: DocMeta) => {
    if (!pickingFor) return;

    try {
      // Reconstruct the target list's current state so the delta is correct
      const updates = await invoke<string[]>("load_updates", { docId: listDoc.docId });
      const tempDoc = new Y.Doc();
      for (const b64 of updates) {
        Y.applyUpdate(tempDoc, toUint8Array(b64), "load");
      }

      const itemName = pickingFor.measurement
        ? `${pickingFor.measurement} ${pickingFor.ingredient}`
        : pickingFor.ingredient;

      // Append the item and capture ONLY the incremental delta (not the full state)
      const deltaB64 = appendListItemDelta(tempDoc, itemName, memberId);
      tempDoc.destroy();

      await invoke("save_update", { docId: listDoc.docId, updateB64: deltaB64 });

      setAddedMsg(`Added to ${listDoc.title}`);
      setTimeout(() => setAddedMsg(""), 2000);
    } catch (e) {
      console.error("Failed to add to list:", e);
    }

    setPickingFor(null);
  };

  return (
    <div className="container" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, padding: "16px" }}>
      <PageHeader title={doc.title} onClose={onBack} style={{ marginBottom: "6px" }}>
        <HelpTip content={mealHelpMd} />
      </PageHeader>

      <div className="meal-photo-section">
        {photoUrl ? (
          <div className="meal-photo-wrap">
            <DecryptedImage token={photoUrl} alt={doc.title} className="meal-photo" onClick={() => photoInputRef.current?.click()} />
            <button
              className="meal-photo-delete"
              onClick={() => {
                if (!yDoc) return;
                yDoc.transact(() => {
                  const yPhoto = yDoc.getText("photo_url");
                  yPhoto.delete(0, yPhoto.toString().length);
                  const yThumb = yDoc.getText("thumb_url");
                  yThumb.delete(0, yThumb.toString().length);
                });
              }}
            >&times;</button>
            {uploadingPhoto && <div className="meal-photo-uploading">...</div>}
          </div>
        ) : (
          <button
            className="meal-photo-btn"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhoto}
          >
            <svg xmlns="http://www.w3.org/2000/svg" height="28px" viewBox="0 -960 960 960" width="28px" fill="currentColor">
              <path d="M480-260q75 0 127.5-52.5T660-440q0-75-52.5-127.5T480-620q-75 0-127.5 52.5T300-440q0 75 52.5 127.5T480-260Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240l74 80h126q33 0 56.5 23.5T880-680v480q0 33-23.5 56.5T800-120H160Zm0-80h640v-480H638l-73-80H395l-73 80H160v480Zm320-240Z"/>
            </svg>
            {uploadingPhoto ? "Uploading..." : "Add Photo"}
          </button>
        )}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoUpload}
          style={{ display: "none" }}
        />
      </div>

      {editing ? (
        <div className="note-editor-scroll">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={() => setEditing(false)}
            placeholder="Describe the meal and how to make it ..."
            className="note-editor"

          />
        </div>
      ) : (
        <div
          className="meal-description"
          onClick={() => setEditing(true)}
        >
          {content || <span style={{ color: "#999" }}>Describe the meal and how to make it ...</span>}
        </div>
      )}

      {!showIngredients ? (
        <Button
          variant="secondary"
          size="full"
          onClick={() => setUserExpandedIngredients(true)}
          style={{ marginTop: "12px" }}
        >
          Enter Ingredients
        </Button>
      ) : (
        <IngredientsSection
          ingredients={ingredients}
          newMeasurement={newMeasurement}
          newIngredient={newIngredient}
          onMeasurementChange={setNewMeasurement}
          onIngredientChange={setNewIngredient}
          onAdd={addIngredient}
          onRemove={removeIngredient}
          onOpenListPicker={openListPicker}
          addedMsg={addedMsg}
        />
      )}

      <ChatBox
        messages={messages}
        memberAvatars={memberAvatars}
        onSend={sendMessage}
        title="Chat about this meal"
      />

      <ListPickerModal
        pickingFor={pickingFor}
        lists={lists}
        onClose={() => setPickingFor(null)}
        onSelect={addToList}
      />
    </div>
  );
}
